import { Link } from "react-router-dom";
import Logo from "../../assets/Logo.svg";
import {
  Youtube,
  Facebook,
  Linkedin,
  MapPin,
  PhoneCall,
  MailOpen,
  AlarmClock,
  Instagram,
} from "lucide-react";
import Button from "../Shared/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { SendNewsletter } from "../../api/Emails/SendNewsletts";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ButtonTypes } from "../../utils/consts";
import { toast } from "react-toastify";
const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

const footerLinks = [
  { text: "Home", url: "/" },
  { text: "About Us", url: "/about-us" },
  { text: "Courses", url: "/courses" },
  { text: "FAQs", url: "/faqs" },
  { text: "Contact Us", url: "/contact-us" },
];

const Footer = () => {
  const { mutate, error, isPending } = useMutation({
    mutationFn: SendNewsletter, // Updated to include salesLogin
    onSuccess: () => {
        toast.success("Email sent successfully");
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema,
    onSubmit: (values) => {
      mutate(values);
    },
  });

  return (
    <footer className="bg-[#F3F5F6] border-t border-black/20">
      <div className="sm:pt-16 pt-12 sm:pb-14 pb-9  container flex justify-between gap-4 flex-wrap">
        <div className="max-w-[285px]">
          <Link to="/">
            <img className="w-[160px]" src={Logo} alt="NileWiseCT" />
          </Link>
          <p className="mt-6 ">
            Our philosophy is simple — hire a team of diverse, passionate people
            and foster a culture that empowers you to do you best work.
          </p>
          <div className="flex gap-5 items-center sm:pt-8  pt-4">
            <Link to="https://www.facebook.com/NileWiseCT" target="_blank">
              <Facebook className="text-burntBlack duration-300 transition-all ease-in-out hover:text-secondary" />
            </Link>
            <Link
              to="https://www.linkedin.com/company/nilewisect/"
              target="_blank"
            >
              <Linkedin className="text-burntBlack duration-300 transition-all ease-in-out hover:text-secondary" />
            </Link>
            <Link to="https://www.youtube.com/@NileWiseCT" target="_blank">
              <Youtube className="text-burntBlack duration-300 transition-all ease-in-out hover:text-secondary" />
            </Link>
            <Link to="https://www.instagram.com/nile_wise_ct/" target="_blank">
              <Instagram className="text-burntBlack duration-300 transition-all ease-in-out hover:text-secondary" />
            </Link>
          </div>
        </div>
        <div className="flex flex-col sm:gap-4 gap-3 ">
          <h1 className="font-semibold sm:text-2xl text-xl pb-2">
            Quick Links
          </h1>
          {footerLinks.map((link, index) => (
            <Link
              key={index}
              to={link.url}
              className="font-medium transition-all sm:text-base text-sm duration-300  hover:text-secondary hover:translate-x-5"
            >
              {link.text}
            </Link>
          ))}
        </div>
        <div className="flex flex-col sm:gap-6 gap-3 max-w-[300px]">
          <h1 className="font-semibold sm:text-2xl text-xl">Our Address</h1>
          <Link
            className="group flex gap-2"
            to={"https://maps.app.goo.gl/Ja4dKtTArWVA8o5J8"}
          >
            <MapPin
              size={32}
              className="sm:min-w-8 sm:w-auto w-6 min-w-6 text-secondary"
            />
            <h1 className="font-semibold group-hover:text-secondary duration-300 transition-all sm:text-base text-sm">
              20 Dinkrat St, El-Shatebi, Alexandria Governorate, Egypt
            </h1>
          </Link>
          <Link className="group flex gap-2" to={"https://wa.me/+201070191322"}>
            <PhoneCall
              size={32}
              className="sm:min-w-8 sm:w-auto w-6 min-w-6 text-secondary"
            />
            <h1 className="font-semibold group-hover:text-secondary duration-300 transition-all sm:text-base text-sm">
              +201070191322
            </h1>
          </Link>
          <Link className="group flex gap-2" to={"mailto:info@nilewisect.com"}>
            <MailOpen
              size={32}
              className="sm:min-w-8 sm:w-auto w-6 min-w-6 text-secondary"
            />
            <h1 className="font-semibold group-hover:text-secondary duration-300 transition-all sm:text-base text-sm">
              info@nilewisect.com
            </h1>
          </Link>

          <div></div>
        </div>
        <div className="flex flex-col sm:gap-4 gap-3 sm:max-w-[300px]">
          <h1 className="font-semibold sm:text-2xl text-xl">NewsLetter</h1>
          <h2 className="font-medium text-textSecondary sm:text-xl">
            Subscribe our Newsletter
          </h2>
          <form onSubmit={formik.handleSubmit} className="">
            <div className="sm:mb-6 mb-3">
            <input
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="email"
              type="text"
              className="sm:h-[50px] h-11 w-full hover:border-secondary focus:border-secondary border transition-all duration-300 focus:outline-none pl-5"
              placeholder="Email@example.com"
            />
            <span className="text-red-500 sm:text-base text-xs">
              {formik.touched.email && formik.errors.email
                ? formik.errors.email
                : ""}
            </span>
            </div>
            <Button
              type={ButtonTypes.SUBMIT}
              disabled={formik.isSubmitting || isPending}
              variant="primary"
              className="rounded-none w-full sm!:h-[50px] !h-11 sm:!text-base !text-sm"
            >
              Submit
              {isPending ? (
                <svg
                  aria-hidden="true"
                  className="w-8 h-8 text-gray-200 ml-3 animate-spin inline dark:text-gray-600 fill-secondary"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              ) : (
                ""
              )}
            </Button>
            {error instanceof AxiosError && (
              <span className="text-red-500 font-bold text-lg">
                {error?.response?.data?.message || "An error occurred"}
              </span>
            )}

            <div className="flex gap-4 mt-9">
              <div className="bg-secondary flex items-center justify-center sm:min-w-16 sm:size-16 size-14">
                <AlarmClock
                  size={40}
                  className="sm:min-w-10 sm:w-auto w-9 text-white"
                />
              </div>
              <h1 className="font-medium sm:text-base">
                From Sunday to Thrusday (9.00 AM - 5.00 PM)
              </h1>
            </div>
          </form>
        </div>
      </div>
      <div className="h-16 text-white text-center bg-secondary flex items-center justify-center font-semibold sm:text-base text-sm px-4">
        ©2024 Nile Wise Training & Consulting. All Rights Reserved.
      </div>
    </footer>
  );
};

export default Footer;
